import type { T } from "../../i18n/kt";
import { rupees } from "../../rates/rateModel";
import { fmtDateTime, num, refusalText } from "../money";
import type { CashEffect, Doubt, StoryItem } from "../types";

/** Owner/manager wording shared by the check, decision and worker screens (UX_COPY frames 6-9). */

const readAuth = (): any => {
  try {
    return JSON.parse(localStorage.getItem("authUser") || "null")?.users || {};
  } catch {
    return {};
  }
};

/** The signed-in user's id (`users._id`, older payloads `users.id`). */
export const myUserId = (): string => String(readAuth()?._id || readAuth()?.id || "");

/** Only the owner (ORGANIZATION_ADMIN) may "Verify anyway" / "Approve anyway" with a reason (D14, D16). */
export const isOwnerRole = (role?: string | null): boolean => role === "ORGANIZATION_ADMIN" || role === "OWNER";

/** "+₹300" / "−₹300" / "₹0". */
export const signedRupees = (x: number | string | null | undefined): string => {
  const n = num(x);
  if (n > 0) return `+${rupees(n)}`;
  if (n < 0) return `−${rupees(-n)}`;
  return rupees(0);
};

export const DOUBT_LABEL: Partial<Record<Doubt, string>> = {
  PAID_TWICE: "Paid twice",
  OVER_DUE: "More than due",
  PAY_CHANGED: "Pay changed after print",
  PHOTO_AFTER_CHECK: "Photo came after checking started",
  SPOT_CHECK: "Spot check",
  RECORDED_BY_YOU: "You recorded this",
  YOUR_OWN_PAY: "Your own pay",
};

export const doubtText = (d: Doubt, t: T, edit?: { n: number; first: string | null }): string => {
  if (d === "EDITED") return edit?.first ? t("Edited {{n}}×, first {{amount}}", { n: edit.n, amount: rupees(edit.first) }) : t("Edited {{n}}×", { n: edit?.n ?? 1 });
  return t(DOUBT_LABEL[d] || d);
};

/**
 * The money effect of a decision or correction (D17): "Ramesh's cash in hand +₹300 · Mohan: ₹300 more
 * due, goes on the next sheet". Parts that did not move are left out.
 */
export const effectLine = (e: CashEffect | null | undefined, personName: string | null, t: T): string => {
  if (!e) return "";
  const parts: string[] = [];
  if (e.holder && num(e.cashInHandDelta) !== 0) parts.push(t("{{name}}'s cash in hand {{delta}}", { name: e.holder.name, delta: signedRupees(e.cashInHandDelta) }));
  const due = num(e.wagesDueDelta);
  const who = personName || t("The worker");
  if (due > 0) parts.push(t("{{name}}: {{amount}} more due, goes on the next sheet", { name: who, amount: rupees(due) }));
  if (due < 0) parts.push(t("{{name}}: {{amount}} less due", { name: who, amount: rupees(-due) }));
  return parts.join(" · ");
};

const REASON_WORD: Record<string, string> = {
  WRONG_AMOUNT: "Wrote wrong",
  NOT_PAID_FULL: "Didn't pay in full",
  DOUBLE: "Written twice",
  OTHER_PERSON: "Was someone else's",
  OTHER: "Something else",
  GOT_LESS: "Got less",
  NOT_RECEIVED: "Didn't get it",
};

export const reasonWord = (code: string | null | undefined, t: T): string => (code && REASON_WORD[code] ? t(REASON_WORD[code]) : code || "");

/**
 * One line of a record's story for owners (frame 9): "₹500 paid · entered by Ramesh · 30 Oct, 5:40 pm ·
 * from PS-0139 photo", "Verified by Priya · 31 Oct, 9:15 am", "Ramesh asked to change ₹500 → ₹200 · … · "why"".
 */
export const ownerStoryLine = (s: StoryItem, t: T): string | null => {
  const when = fmtDateTime(s.at);
  const who = s.actor?.name || "";
  const before = s.before !== null ? rupees(s.before) : "";
  const after = s.after !== null ? rupees(s.after) : "";
  const why = s.reason ? `"${s.reason}"` : s.reasonCode ? reasonWord(s.reasonCode, t) : "";
  const from = s.source === "PHOTO" && s.sheetNo ? t("from {{sheetNo}} photo", { sheetNo: s.sheetNo }) : s.source === "MCP" ? t("through the assistant") : "";
  const join = (...xs: string[]) => xs.filter(Boolean).join(" · ");
  switch (s.action) {
    case "BASELINE":
      return join(t("{{amount}} before the cash book started", { amount: after }), when);
    case "RECORDED":
    case "ROW_ENTERED":
      return join(s.after === "0" || s.after === "" ? t("Not paid (left blank)") : t("{{amount}} paid", { amount: after }), t("entered by {{name}}", { name: who }), when, from);
    case "CORRECTED":
      return join(t("{{name}} changed {{before}} → {{after}}", { name: who, before, after }), when, why);
    case "VERIFIED":
      return join(t("Verified by {{name}}", { name: who }), when);
    case "VERIFY_OVERRIDE":
      return join(t("Verified by {{name}} (self-verified)", { name: who }), when, why);
    case "CHANGE_PROPOSED":
      return join(s.after === null ? t("{{name}} asked to remove {{before}}", { name: who, before }) : t("{{name}} asked to change {{before}} → {{after}}", { name: who, before, after }), when, why);
    case "CHANGE_APPROVED":
      return join(
        s.after === null ? t("{{name}} approved the removal", { name: who }) : t("{{name}} approved {{after}}", { name: who, after }),
        s.selfVerified ? t("self-approved") : "",
        when,
        why
      );
    case "CHANGE_REJECTED":
      return join(t("{{name}} kept {{amount}}", { name: who, amount: before || after }), when, why);
    case "VOIDED":
      return join(t("{{name}} removed it", { name: who }), when, why);
    case "TRANSFER_SENT":
      return join(t("{{name}} gave {{amount}}", { name: who, amount: after }), when);
    case "TRANSFER_CONFIRMED":
      return join(t("{{name}} said received", { name: who }), when);
    case "TRANSFER_REJECTED":
      return join(t("{{name}} said it's wrong", { name: who }), when, why);
    case "TRANSFER_REVISED":
      return join(t("{{name}} gave again: {{amount}}", { name: who, amount: after }), when);
    case "TRANSFER_DISPUTED":
      return join(t("{{name}} told the owner", { name: who }), when, why);
    case "TRANSFER_DISPUTE_CLOSED":
      return join(t("{{name}} closed the dispute", { name: who }), when, why);
    default:
      return null;
  }
};

/** Refusals on owner screens: the codes the check and decisions meet get plain wording (section 9). */
const OWNER_CODE_TEXT: Record<string, string> = {
  NOT_CLEAN_FOR_GROUP: "Verify this row on its own.",
  PHOTO_CHANGED: "A newer photo of this page came in. Look at it before verifying.",
  RECORDED_BY_YOU: "You recorded this. Someone else checks it.",
  YOUR_OWN_PAY: "Your own pay. Someone else checks it.",
  ALREADY_DECIDED: "This change was already decided.",
  REASON_REQUIRED: "Say why.",
  OVER_DUE_REASON: "More than due: pick why (advance, bonus, back pay or wrong).",
  PRINT_SUPERSEDED: "This print was replaced by a newer print. Check the new one.",
  TOO_MANY: "At most 10 at a time.",
  HEAD_EXISTS: "That spend type already exists.",
  TRANSFER_DECIDED: "This handover was already answered.",
};

export const ownerRefusal = (res: { code?: string | null; message?: string | null; data?: any } | null | undefined, t: T): string => {
  const code = res?.code || "";
  if (code === "GAP_NOT_ZERO") {
    const gap = res?.data?.gap;
    return gap !== undefined && gap !== null ? t("Explain {{gap}} first", { gap: rupees(Math.abs(num(gap))) }) : t(res?.message || "Explain the gap first");
  }
  if (OWNER_CODE_TEXT[code]) return t(OWNER_CODE_TEXT[code]);
  return refusalText({ code: code || undefined, message: res?.message || undefined }, t);
};
